import axios from 'axios';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from 'services/firebase';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
  BrandLogo,
  CandidateAskQuestionModal,
  CandidateDetailsActionButton,
  CandidateDetailsContent,
  CandidateDetailsHeader,
  CandidateDetailsPageLayout,
  ClientFeedbackModal,
  ClientRescheduleCancelModal,
  PageWrapper,
  PasswordInput,
} from '@components';

import {
  candidateDetailsSelectors,
  clearCandidateDetails,
  fetchCandidateDetails,
} from '@redux/candidateDetails';
import { setGuestPermissions } from '@redux/accounts';
import { fetchPositions } from '@redux/positions';
import { CandidatesPage } from '../Candidates/Candidates';
import {
  AppRoutes,
  forgotPasswordAPI,
  guestGrantAPI,
  guestRequestAccessAPI,
  guestRequestCodeAPI,
  guestStatusAPI,
  guestVerifyCodeAPI,
} from '@constants';
import { useTitle } from '@hooks';
import {
  formatDate,
  getGuestShareToken,
  guestListPath,
  guestSubmissionPath,
  isEmail,
  isStatusComplete,
  isStatusIdle,
  isStatusLoading,
} from '@utils';

/** The address a code was sent to, kept for this tab until the code is entered. */
const pendingCode = {
  key: () => `guest-code:${getGuestShareToken()}`,
  read: (): string | null => {
    try {
      return window.sessionStorage.getItem(pendingCode.key());
    } catch {
      return null;
    }
  },
  write: (email: string) => {
    try {
      window.sessionStorage.setItem(pendingCode.key(), email);
    } catch {
      // Storage can be unavailable; the guest just types their address again.
    }
  },
  clear: () => {
    try {
      window.sessionStorage.removeItem(pendingCode.key());
    } catch {
      // Nothing to clear.
    }
  },
};

type Stage =
  | 'loading'
  | 'email'
  | 'code'
  | 'account'
  | 'password-reset'
  | 'request'
  | 'requested'
  | 'ready'
  | 'dead';

interface IGrantSubmission {
  id: string;
  candidateName: string;
  positionName: string;
  status: string | null;
}

/** What `GET /s/:token/grant` answers once the guest has proved their address. */
interface IGrant {
  recipientEmail: string;
  expiresAt: string;
  permissions: string[];
  scope: 'client' | 'position' | 'submission';
  scopeLabel: string;
  clientName: string | null;
  sharer: string | null;
  submissions: IGrantSubmission[];
}

/**
 * The dashboard's candidate page, opened by someone without an account through a share
 * link. Everything below the layout is the same component tree a signed-in contact sees:
 * the sidebar and the share control are the only things a guest does not get, and the
 * actions available to them are decided by the same status rules, enforced again server
 * side. What is bespoke here is the gate in front of it and the list of what was shared.
 */
export const GuestSubmissionPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const setPageTitle = useTitle();
  const { submissionId } = useParams();

  const { apiStatus, data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  const [stage, setStage] = useState<Stage>('loading');
  const [grant, setGrant] = useState<IGrant | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  /** CRM knows this address: the request goes without asking for a name. */
  const [nameKnown, setNameKnown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * A guest is verified by a cookie, so an existing session simply loads the grant. A code
   * that was asked for but not yet entered is remembered for this tab: a phone reloads the
   * page when the guest comes back from their mail app, and they should land on the code
   * field, not on the email field again.
   */
  const loadGrant = useCallback(async () => {
    try {
      const { data: loaded } = await axios.get<IGrant>(guestGrantAPI(), {
        withCredentials: true,
      });
      setGrant(loaded);
      dispatch(setGuestPermissions(loaded.permissions));
      pendingCode.clear();
      setStage('ready');
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setStage('dead');

        return;
      }
      const pending = pendingCode.read();
      if (pending) setEmail(pending);
      setStage(pending ? 'code' : 'email');
    }
  }, [dispatch]);

  useEffect(() => {
    void loadGrant();

    return () => {
      dispatch(clearCandidateDetails());
    };
  }, [dispatch, loadGrant]);

  // One candidate in the grant: open it straight away, there is nothing to pick from.
  // The list is the dashboard's own candidates page, fed by the same thunk from the guest base.
  useEffect(() => {
    if (stage === 'ready' && !submissionId) dispatch(fetchPositions());
  }, [dispatch, stage, submissionId]);

  useEffect(() => {
    if (stage === 'ready' && !submissionId && grant?.submissions.length === 1) {
      navigate(guestSubmissionPath(grant.submissions[0].id), { replace: true });
    }
  }, [grant, navigate, stage, submissionId]);

  useEffect(() => {
    if (stage !== 'ready' || !submissionId) return;
    dispatch(fetchCandidateDetails(submissionId));

    return () => {
      dispatch(clearCandidateDetails());
    };
  }, [dispatch, stage, submissionId]);

  useEffect(() => {
    if (submissionId && data?.profile?.firstName && data?.profile?.lastName) {
      setPageTitle(`${data.profile.firstName} ${data.profile.lastName}`);
    } else if (stage === 'ready') {
      setPageTitle('Candidates shared with you');
    }
  }, [data, setPageTitle, stage, submissionId]);

  /**
   * The sharer can revoke the grant at any moment, and the gate session lapses after 30
   * minutes idle or an hour outright. Nothing else would notice until the guest tried to
   * act, so the page asks, and drops back to the gate the moment access ends. Only a real
   * interaction renews idle activity.
   */
  useEffect(() => {
    if (stage !== 'ready') return;
    let interacted = false;
    const note = () => {
      interacted = true;
    };
    const events = ['pointerdown', 'keydown', 'scroll'] as const;
    events.forEach((event) =>
      window.addEventListener(event, note, { passive: true }),
    );

    const timer = window.setInterval(async () => {
      const active = interacted;
      interacted = false;
      try {
        await axios.get(guestStatusAPI(active), { withCredentials: true });
      } catch (e: any) {
        // Only a lost gate sends the guest back; a flaky network does not.
        const status = e?.response?.status;
        if (status !== 401 && status !== 404) return;
        dispatch(clearCandidateDetails());
        setGrant(null);
        setStage('email');
      }
    }, 60000);

    return () => {
      window.clearInterval(timer);
      for (const event of events) {
        window.removeEventListener(event, note);
      }
    };
  }, [dispatch, stage]);

  const requestCode = useCallback(async () => {
    if (busy) return;
    const normalized = email.trim().toLowerCase();
    if (!isEmail(normalized)) {
      setError('Please enter a valid email address.');

      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { data: outcome } = await axios.post<{
        status?: string;
        nameKnown?: boolean;
      }>(
        guestRequestCodeAPI(),
        { email: normalized },
        { withCredentials: true },
      );
      setEmail(normalized);
      setCode('');
      setPassword('');
      // An address with an account signs in instead; one this grant was not made for has to
      // be let in.
      const next =
        outcome?.status === 'has_account'
          ? 'account'
          : outcome?.status === 'approval_required'
            ? 'request'
            : 'code';
      if (next === 'code') pendingCode.write(normalized);
      setNameKnown(Boolean(outcome?.nameKnown));
      setStage(next);
    } catch {
      setError('We could not send the code. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [busy, email]);

  const changeEmail = () => {
    pendingCode.clear();
    setPassword('');
    setCode('');
    setEmail('');
    setError(null);
    setStage('email');
  };

  const signIn = async () => {
    if (busy || !password) return;
    setError(null);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Reload into the authenticated dashboard: the guest API base is selected at
      // module load and must not survive the transition out of the shared link.
      window.location.assign(
        `/${AppRoutes.CANDIDATES}${submissionId ? `/${submissionId}` : ''}`,
      );
    } catch (e: any) {
      setError(
        e?.code === 'auth/too-many-requests'
          ? 'Too many attempts. Please try again later.'
          : e?.code === 'auth/network-request-failed'
            ? 'We could not log you in. Please try again.'
            : 'Incorrect email or password. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await axios.post(forgotPasswordAPI, { username: email });
      setPassword('');
      setStage('password-reset');
    } catch {
      setError('We could not send the email. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = useCallback(async () => {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from the email.');

      return;
    }
    setError(null);
    setBusy(true);
    try {
      await axios.post(
        guestVerifyCodeAPI(),
        { email, code },
        { withCredentials: true },
      );
      await loadGrant();
    } catch {
      setError('The code is invalid or has expired.');
    } finally {
      setBusy(false);
    }
  }, [code, email, loadGrant]);

  const requestAccess = useCallback(async () => {
    if (!nameKnown && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first and last name.');

      return;
    }
    setError(null);
    setBusy(true);
    try {
      await axios.post(
        guestRequestAccessAPI(),
        nameKnown
          ? { email }
          : { email, firstName: firstName.trim(), lastName: lastName.trim() },
        { withCredentials: true },
      );
      setStage('requested');
    } catch {
      setError('We could not send your request. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [email, firstName, lastName, nameKnown]);

  const gate = (title: string, body: React.ReactNode) => (
    <Stack
      minHeight="100dvh"
      alignItems="center"
      justifyContent="center"
      px={2}
      py={4}
      gap={3}
    >
      <BrandLogo />
      <Stack maxWidth={320} width="100%" gap={2}>
        <Typography variant="h2" component="h1">
          {title}
        </Typography>
        {body}
        {error && (
          <Typography color="text.danger" variant="caption">
            {error}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  if (stage === 'loading') {
    return (
      <Stack
        minHeight="100dvh"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (stage === 'dead') {
    return gate(
      'This link has expired or was never shared',
      <Typography variant="body1" color="text.secondary">
        Access to shared candidates lasts seven days. Ask the person who shared
        them to share again.
      </Typography>,
    );
  }

  if (stage === 'email') {
    return gate(
      'View shared candidates',
      <>
        <Typography variant="body1" color="text.secondary">
          Enter your work email to receive a verification code.
        </Typography>
        <TextField
          autoFocus
          label="Your work email"
          type="email"
          value={email}
          placeholder="name@company.com"
          InputLabelProps={{ shrink: true }}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void requestCode();
          }}
        />
        <Button
          variant="contained"
          disabled={!email.trim() || busy}
          onClick={() => void requestCode()}
        >
          Send code
        </Button>
      </>,
    );
  }

  if (stage === 'account') {
    return gate(
      'You already have an account',
      <>
        <Typography variant="body1" color="text.secondary">
          Log in to your Client Dashboard to view these candidates.
        </Typography>
        <Typography variant="body1">
          <strong>{email}</strong>
        </Typography>
        <PasswordInput
          autoFocus
          name="password"
          label="Password"
          value={password}
          autoComplete="current-password"
          disabled={busy}
          error={Boolean(error)}
          onChange={(event) => {
            setPassword(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void signIn();
          }}
        />
        <Button
          variant="contained"
          disabled={busy || !password}
          onClick={() => void signIn()}
        >
          Log in
        </Button>
        <Button variant="text" disabled={busy} onClick={changeEmail}>
          Use a different email
        </Button>
        <Button
          variant="text"
          disabled={busy}
          onClick={() => void resetPassword()}
        >
          Reset your password
        </Button>
      </>,
    );
  }

  if (stage === 'password-reset') {
    return gate(
      'Check your email',
      <>
        <Typography variant="body1" color="text.secondary">
          We sent a link to <strong>{email}</strong> to reset your password.
        </Typography>
        <Button variant="contained" onClick={() => setStage('account')}>
          Back to login
        </Button>
        <Button
          variant="text"
          disabled={busy}
          onClick={() => void resetPassword()}
        >
          Resend link
        </Button>
        <Button variant="text" disabled={busy} onClick={changeEmail}>
          Use a different email
        </Button>
      </>,
    );
  }

  if (stage === 'request') {
    return gate(
      'This link was shared with someone else',
      <>
        <Typography variant="body1" color="text.secondary">
          Access was given to a different address, so we cannot show{' '}
          <strong>{email}</strong> anything yet.{' '}
          {nameKnown
            ? 'We can pass your request to your AgileEngine account manager.'
            : 'Tell us who you are and we will pass your request to your AgileEngine account manager.'}
        </Typography>
        {!nameKnown && (
          <>
            <TextField
              autoFocus
              label="First name"
              value={firstName}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => {
                setFirstName(event.target.value);
                setError(null);
              }}
            />
            <TextField
              label="Last name"
              value={lastName}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => {
                setLastName(event.target.value);
                setError(null);
              }}
            />
          </>
        )}
        <Button
          variant="contained"
          disabled={
            busy || (!nameKnown && (!firstName.trim() || !lastName.trim()))
          }
          onClick={() => void requestAccess()}
        >
          Ask for access
        </Button>
        <Button variant="text" onClick={() => setStage('email')}>
          Use a different email
        </Button>
      </>,
    );
  }

  if (stage === 'requested') {
    return gate(
      'Your request was sent',
      <Typography variant="body1" color="text.secondary">
        We have sent it to your AgileEngine account manager. Once they give
        access, you will get an email at <strong>{email}</strong> with your own
        links.
      </Typography>,
    );
  }

  if (stage === 'code') {
    return gate(
      'Check your email',
      <>
        <Typography variant="body1" color="text.secondary">
          Enter the code sent to <strong>{email}</strong>. It expires in 10
          minutes.
        </Typography>
        <TextField
          autoFocus
          label="6-digit code"
          value={code}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void verifyCode();
          }}
        />
        <Button
          variant="contained"
          disabled={code.length !== 6 || busy}
          onClick={() => void verifyCode()}
        >
          Continue
        </Button>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="text"
            onClick={() => {
              pendingCode.clear();
              setStage('email');
            }}
          >
            Change email
          </Button>
          <Button
            variant="text"
            disabled={busy}
            onClick={() => void requestCode()}
          >
            Resend code
          </Button>
        </Box>
      </>,
    );
  }

  if (!grant) return null;

  const expiresOn = formatDate(grant.expiresAt);
  const sharedBy = [grant.sharer, grant.clientName]
    .filter(Boolean)
    .join(' at ');

  /**
   * The candidate page sits under a corner logo; the list, being full width, under a logo row.
   * The frame is the viewport, as `AppLayout` is for the dashboard: the pages inside scroll
   * their own boxes, which keeps the table headers pinned.
   */
  const frame = (children: React.ReactNode, { fullWidth = false } = {}) => (
    <PageWrapper>
      <Stack
        height="100dvh"
        position="relative"
        sx={(theme) => ({
          background: theme.palette.background.backgroundPrimary,
        })}
      >
        <Box
          sx={{
            position: {
              xs: 'relative',
              md: fullWidth ? 'relative' : 'absolute',
            },
            top: 0,
            left: 0,
          }}
          pt={{ xs: 2, md: 3 }}
          pl={{ xs: 2, md: 6 }}
        >
          <BrandLogo />
        </Box>
        <Stack
          alignItems={fullWidth ? 'stretch' : 'center'}
          flex={1}
          minHeight={0}
          pt={{ xs: 1, md: 1.5 }}
        >
          {children}
        </Stack>
      </Stack>
    </PageWrapper>
  );

  // The list of what was shared is the dashboard's own candidates page: the same table,
  // filters and drawer, reading the grant through the guest base.
  if (!submissionId) {
    if (!grant.submissions.length) {
      return frame(
        <Stack width="100%" maxWidth={320} px={2} py={6} gap={2}>
          <Typography variant="h2" component="h1">
            Candidates shared with you
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Nothing shared with you is open any more.
          </Typography>
        </Stack>,
      );
    }

    return frame(
      <CandidatesPage
        title="Candidates shared with you"
        intro={
          <>
            {sharedBy ? `${sharedBy} shared ` : 'You were given access to '}
            {grant.submissions.length}{' '}
            {grant.submissions.length === 1 ? 'candidate' : 'candidates'}
            {grant.scope === 'position' && ` for ${grant.scopeLabel}`}. Access
            lasts until {expiresOn}.
          </>
        }
      />,
      { fullWidth: true },
    );
  }

  if (isStatusLoading(apiStatus) || isStatusIdle(apiStatus)) {
    return frame(
      <Stack flex={1} alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>,
    );
  }

  if (!isStatusComplete(apiStatus) || !data) {
    return frame(
      <Stack width="100%" maxWidth={320} px={2} py={6} gap={2}>
        <Typography variant="h2" component="h1">
          This candidate is unavailable
        </Typography>
        <Typography variant="body1" color="text.secondary">
          It is no longer part of what was shared with you.
        </Typography>
        {grant.submissions.length > 1 && (
          <Button
            component={RouterLink}
            to={guestListPath()}
            variant="outlined"
          >
            Back to the shared candidates
          </Button>
        )}
      </Stack>,
    );
  }

  return (
    <>
      {frame(
        <CandidateDetailsPageLayout
          hideBreadcrumb
          headerComponent={
            <Stack gap={2}>
              {grant.submissions.length > 1 && (
                <Link
                  component={RouterLink}
                  to={guestListPath()}
                  underline="always"
                  color="text.link"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    alignSelf: 'flex-start',
                    gap: 0.75,
                    typography: 'body2',
                    textUnderlineOffset: '2px',
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 14 }} />
                  All shared candidates
                </Link>
              )}
              <CandidateDetailsHeader data={data} />
            </Stack>
          }
          contentComponent={<CandidateDetailsContent data={data} />}
          footerComponent={
            <CandidateDetailsActionButton
              data={data}
              sx={{ px: 2, py: 1.5, justifyContent: 'flex-end' }}
            />
          }
        />,
      )}
      <ClientFeedbackModal />
      <ClientRescheduleCancelModal />
      <CandidateAskQuestionModal />
    </>
  );
};
